/**
 * Static configuration using Vite environment variables
 * These are set by Terraform as Azure SWA application settings
 * and become available as build-time environment variables
 */

export interface AppConfig {
    // Auth0 configuration (new platform auth)
    auth0Domain: string;
    auth0ClientId: string;
    auth0Audience?: string; // Optional - only needed if using JWT validation instead of /userinfo

    // Palantir configuration (legacy - for OSDK during migration)
    palantirClientId: string;
    palantirAuxiliaryClientId: string;
    palantirFoundryUrl: string;
    palantirOntologyRid: string;
    palantirRedirectUrl: string;
    palantirAuxiliaryRedirectUrl: string;

    // Agent configuration
    externalCompanyAgentRid: string;
    externalCompanyAgentVersion: string;
    internalCompanyAgentRid: string;
    internalCompanyAgentVersion: string;

    // Service URLs
    integrationsManagerUrl: string;
    userManagementUrl: string;
    apiUrl: string;

    // Optional
    customerLogoPath?: string;
    enableApiDebugPage?: boolean;

    // Session timeout configuration (in minutes)
    sessionIdleTimeoutMinutes: number;
    sessionWarningBeforeTimeoutMinutes: number;
}

// Static configuration object - values are injected at build time by Vite
export const config: AppConfig = {
    // Auth0 configuration
    auth0Domain: import.meta.env.VITE_AUTH0_DOMAIN || "",
    auth0ClientId: import.meta.env.VITE_AUTH0_CLIENT_ID || "",
    auth0Audience: import.meta.env.VITE_AUTH0_AUDIENCE || "",

    // Palantir configuration
    palantirClientId: import.meta.env.VITE_PALANTIR_CLIENT_ID || "",
    palantirAuxiliaryClientId:
        import.meta.env.VITE_PALANTIR_AUXILIARY_CLIENT_ID || "",
    palantirFoundryUrl:
        import.meta.env.VITE_PALANTIR_FOUNDRY_URL ||
        "https://helmguardai.euw-3.palantirfoundry.co.uk",
    palantirOntologyRid: import.meta.env.VITE_PALANTIR_ONTOLOGY_RID || "",
    palantirRedirectUrl:
        import.meta.env.VITE_PALANTIR_REDIRECT_URL ||
        `${import.meta.env.VITE_FRONTEND_URL || window.location.origin}/auth/palantir-callback`,
    palantirAuxiliaryRedirectUrl:
        import.meta.env.VITE_PALANTIR_AUXILIARY_REDIRECT_URL ||
        `${import.meta.env.VITE_FRONTEND_URL || window.location.origin}/auth/auxiliary-callback`,

    // Agent configuration
    externalCompanyAgentRid:
        import.meta.env.VITE_EXTERNAL_COMPANY_AGENT_RID || "",
    externalCompanyAgentVersion:
        import.meta.env.VITE_EXTERNAL_COMPANY_AGENT_VERSION || "",
    internalCompanyAgentRid:
        import.meta.env.VITE_INTERNAL_COMPANY_AGENT_RID || "",
    internalCompanyAgentVersion:
        import.meta.env.VITE_INTERNAL_COMPANY_AGENT_VERSION || "",

    // Service URLs
    integrationsManagerUrl: import.meta.env.VITE_INTEGRATIONS_MANAGER_URL || "",
    userManagementUrl: import.meta.env.VITE_USER_MANAGEMENT_URL || "",
    apiUrl: import.meta.env.VITE_API_SERVICE_URL || "",

    // Optional
    customerLogoPath: import.meta.env.VITE_CUSTOMER_LOGO_PATH,
    enableApiDebugPage: import.meta.env.VITE_ENABLE_API_DEBUG_PAGE === "true",

    // Session timeout configuration (in minutes)
    // IMPORTANT: sessionIdleTimeoutMinutes should match backend SESSION_IDLE_TIMEOUT_SECONDS / 60
    // Backend enforces the timeout; frontend provides UX warnings before timeout occurs
    sessionIdleTimeoutMinutes: parseInt(
        import.meta.env.VITE_SESSION_IDLE_TIMEOUT_MINUTES || "60",
        10,
    ),
    sessionWarningBeforeTimeoutMinutes: parseInt(
        import.meta.env.VITE_SESSION_WARNING_MINUTES || "5",
        10,
    ),
};

// Validate required fields at module load time
const requiredFields: (keyof AppConfig)[] = [
    // Auth0 configuration
    "auth0Domain",
    "auth0ClientId",
    // "auth0Audience" - optional, only needed for JWT validation
    // Palantir configuration (still required during migration)
    "palantirClientId",
    "palantirAuxiliaryClientId",
    "palantirFoundryUrl",
    "palantirOntologyRid",
    // Agent configuration
    "externalCompanyAgentRid",
    "externalCompanyAgentVersion",
    "internalCompanyAgentRid",
    "internalCompanyAgentVersion",
    // Service URLs
    "integrationsManagerUrl",
    "userManagementUrl",
    "apiUrl",
];

const missingFields = requiredFields.filter((field) => !config[field]);
if (missingFields.length > 0) {
    console.error(
        `Missing required configuration fields: ${missingFields.join(", ")}`,
    );
    // Don't throw in production to avoid breaking the app
    if (import.meta.env.DEV) {
        throw new Error(
            `Missing required configuration: ${missingFields.join(", ")}`,
        );
    }
} else {
    console.log("Configuration loaded successfully");
}
